import { ConflictError, PermissionError } from "./errors.js";

const byId = rows => new Map((rows || []).map(row => [row.id, row]));
const codeById = rows => new Map((rows || []).map(row => [row.id, row.code]));
const asNumber = value => Number(value || 0);
const withoutUndefined = object => Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined));

/* An Edge Function that fails says why in its body, and `functions.invoke`
   throws that body away: a non-2xx becomes a bare FunctionsHttpError reading
   "Edge Function returned a non-2xx status code", with the reason still sitting
   unread in `error.context`. That is how "OPENAI_API_KEY secret is not
   configured" reached the user as "매칭이 안 된다" with nothing to act on.

   So every invocation is unwrapped through here, and the function's own message
   is what surfaces. */
async function invokeFunction(client, name, body) {
  const { data, error } = await client.functions.invoke(name, { body });
  if (error) {
    let detail = "";
    try {
      const response = error.context;
      if (response && typeof response.json === "function") {
        const payload = await response.clone().json().catch(async () => ({ error: await response.clone().text().catch(() => "") }));
        detail = payload?.error || payload?.message || "";
      }
    } catch { /* the body is unreadable; the generic message is all there is */ }
    throw new Error(detail || error.message || `${name} failed`);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export class SupabaseDataService {
  constructor(client, context) {
    this.client = client;
    this.mode = "cloud";
    this.session = context.session;
    this.profile = context.profile;
    this.workspaceId = context.workspaceId;
    this.systemRole = context.systemRole;
    this.config = context.config;
    this.projectScope = "ALL";
    this.dataChannel = null;
    this.presenceChannel = null;
    this.changeTimer = null;
  }

  async initialize() {
    const seenAt = new Date().toISOString();
    const { error: seenError } = await this.client.from("profiles").update({ last_seen_at: seenAt }).eq("id", this.profile.id);
    if (!seenError) this.profile.last_seen_at = seenAt;
    this.state = await this.loadAll();
    return this.state;
  }

  async reload() {
    this.state = await this.loadAll();
    return this.state;
  }

  async selectWorkspaceTable(table, select = "*") {
    const { data, error } = await this.client
      .from(table)
      .select(select)
      .eq("workspace_id", this.workspaceId)
      .is("deleted_at", null);
    if (error) throw error;
    return data || [];
  }

  async loadAll() {
    const tableNames = [
      "programs", "projects", "phases", "activities", "activity_dependencies",
      "outcomes", "deliverables", "gates", "gate_checklists", "resources",
      "resource_assignments", "costs", "organizations", "mou_actions", "events", "risks",
      "decisions", "profiles", "workspace_members", "project_members", "attachments",
      "strategic_goals", "evidence_items", "entity_links", "intelligence_predictions", "graph_views",
      "atlas_node_positions", "ideas", "tea_projects"
    ];
    const results = await Promise.all(tableNames.map(table => this.selectWorkspaceTable(table)));
    const raw = Object.fromEntries(tableNames.map((table, index) => [table, results[index]]));
    const { data: calendarLinks, error: calendarLinksError } = await this.client
      .from("google_calendar_event_links")
      .select("*")
      .eq("workspace_id", this.workspaceId);
    if (calendarLinksError) throw calendarLinksError;
    raw.google_calendar_event_links = calendarLinks || [];
    const { data: audits, error: auditError } = await this.client
      .from("audit_logs")
      .select("*")
      .eq("workspace_id", this.workspaceId)
      .order("changed_at", { ascending: false })
      .limit(250);
    if (auditError) throw auditError;
    raw.audit_logs = audits || [];

    const projectCodes = codeById(raw.projects);
    const phaseCodes = codeById(raw.phases);
    const activityCodes = codeById(raw.activities);
    const gateCodes = codeById(raw.gates);
    const resourceCodes = codeById(raw.resources);
    const organizationCodes = codeById(raw.organizations);
    const program = raw.programs[0];
    if (!program) throw new Error("접근 가능한 NAYSOR Program이 없습니다.");
    this.programId = program.id;
    this.maps = {
      projects: byId(raw.projects),
      phases: byId(raw.phases),
      activities: byId(raw.activities),
      gates: byId(raw.gates),
      resources: byId(raw.resources),
      organizations: byId(raw.organizations)
    };
    this.codeMaps = { projectCodes, phaseCodes, activityCodes, gateCodes, resourceCodes, organizationCodes };
    const dependenciesBySuccessor = new Map();
    raw.activity_dependencies.forEach(dependency => {
      const successor = activityCodes.get(dependency.successor_activity_id);
      const predecessor = activityCodes.get(dependency.predecessor_activity_id);
      if (!successor || !predecessor) return;
      const suffix = dependency.relationship_type && dependency.relationship_type !== "FS"
        ? ":" + dependency.relationship_type + (dependency.lag_days ? (dependency.lag_days > 0 ? "+" : "") + dependency.lag_days : "")
        : (dependency.lag_days ? ":FS" + (dependency.lag_days > 0 ? "+" : "") + dependency.lag_days : "");
      const values = dependenciesBySuccessor.get(successor) || [];
      values.push(predecessor + suffix);
      dependenciesBySuccessor.set(successor, values);
    });
    const workspaceMembershipByProfile = new Map((raw.workspace_members || []).map(row => [row.profile_id, row]));

    const checklistByGate = new Map();
    raw.gate_checklists.forEach(item => {
      const values = checklistByGate.get(item.gate_id) || [];
      values.push({
        id: item.code,
        _dbId: item.id,
        version: item.version,
        text: item.item_text,
        required: item.required,
        completed: item.completed,
        owner: item.owner_text || "",
        due: item.due_date || "",
        evidence: item.evidence_text || "",
        updatedAt: item.updated_at,
        updatedBy: item.updated_by
      });
      checklistByGate.set(item.gate_id, values);
    });

    const state = {
      meta: {
        schemaVersion: program.schema_version,
        appVersion: program.app_version,
        programCode: program.code,
        programName: program.name,
        programObjective: program.objective || "",
        programOwner: program.owner_text || "",
        programStart: program.start_date || "",
        programFinish: program.finish_date || "",
        programDbId: program.id,
        programVersion: program.version,
        asOfDate: program.as_of_date,
        currency: program.currency,
        calendar: program.calendar_name,
        baseline: program.active_baseline_code || "BL-01",
        storageMode: "SUPABASE CLOUD",
        lastSaved: program.updated_at
      },
      projects: raw.projects.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, code: row.code,
        name: row.name, englishName: row.name_en || row.name, objective: row.objective || "",
        owner: row.owner_text || "", sponsor: row.sponsor_text || "", color: row.color || "#245f88",
        start: row.start_date || "", finish: row.finish_date || "", budget: asNumber(row.budget),
        status: row.status, completionGateId: row.completion_gate_code || "",
        updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      phases: raw.phases.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, code: row.code,
        projectId: projectCodes.get(row.project_id), order: row.order_no,
        name: row.name, koreanName: row.name_ko || row.name, objective: row.objective || "",
        owner: row.owner_text || "", start: row.start_date || "", finish: row.finish_date || "",
        outcomeId: row.outcome_code || "", updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      activities: raw.activities.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, wbs: row.wbs_code || "",
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id),
        name: row.name, start: row.start_date || "", finish: row.finish_date || "",
        baselineStart: row.baseline_start || row.start_date || "", baselineFinish: row.baseline_finish || row.finish_date || "",
        actualStart: row.actual_start || "", actualFinish: row.actual_finish || "",
        forecastStart: row.forecast_start || "", forecastFinish: row.forecast_finish || "",
        progress: asNumber(row.progress), status: row.status, owner: row.owner_text || "",
        assignedTo: row.assigned_to || "", organization: row.organization_text || "",
        predecessors: (dependenciesBySuccessor.get(row.code) || []).join(","),
        gateId: row.gate_code || "", priority: row.priority || "P1",
        productStream: row.product_stream || "Shared", budget: asNumber(row.budget),
        actualCost: asNumber(row.actual_cost), forecastCost: asNumber(row.forecast_cost),
        plannedHours: asNumber(row.planned_hours), actualHours: asNumber(row.actual_hours),
        description: row.description || "", kpi: row.kpi || "", calendar: row.calendar_name || "NAYSOR-5D",
        constraint: row.constraint_type || "As Soon As Possible", notes: row.notes || "",
        source: row.source || "", updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      outcomes: raw.outcomes.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, code: row.code,
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id),
        name: row.name, kpi: row.kpi || "", target: row.target_value || "",
        current: row.current_value || "", unit: row.unit || "", status: row.status
      })),
      deliverables: raw.deliverables.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, code: row.code,
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id),
        activityId: activityCodes.get(row.activity_id) || "", name: row.name, type: row.deliverable_type || "",
        owner: row.owner_text || "", due: row.due_date || "", status: row.status,
        approvalStatus: row.approval_status, versionLabel: row.revision || "v0.1",
        version: row.version, fileRef: row.external_url || "", gateId: gateCodes.get(row.gate_id) || "",
        notes: row.notes || "", updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      gates: raw.gates.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, code: row.code,
        projectId: projectCodes.get(row.project_id), afterPhaseId: phaseCodes.get(row.after_phase_id) || "",
        beforePhaseId: phaseCodes.get(row.before_phase_id) || "", name: row.name,
        targetDate: row.target_date || "", approver: row.approver_text || "",
        status: row.status, decision: row.decision || "", decisionDate: row.decision_date || "",
        notes: row.notes || "", checklist: checklistByGate.get(row.id) || [],
        updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      resources: raw.resources.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, name: row.name, type: row.resource_type,
        organization: row.organization_text || "", role: row.role_text || "",
        capacityHoursMonth: asNumber(row.capacity_hours_month), rate: asNumber(row.rate),
        currency: row.currency || program.currency
      })),
      assignments: raw.resource_assignments.map(row => ({
        id: row.code, _dbId: row.id, version: row.version,
        activityId: activityCodes.get(row.activity_id), resourceId: resourceCodes.get(row.resource_id),
        plannedHours: asNumber(row.planned_hours), actualHours: asNumber(row.actual_hours), role: row.role_text || ""
      })),
      costs: raw.costs.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, code: row.code,
        projectId: projectCodes.get(row.project_id) || "", activityId: activityCodes.get(row.activity_id) || "",
        costType: row.cost_type || "Direct", budget: asNumber(row.budget), plannedValue: asNumber(row.planned_value),
        earnedValue: asNumber(row.earned_value), actualCost: asNumber(row.actual_cost), forecastCost: asNumber(row.forecast_cost),
        currency: row.currency || program.currency, dataDate: row.data_date || "",
        updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      organizations: raw.organizations.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, name: row.name,
        type: row.organization_type || "", country: row.country || "", contact: row.contact_name || "",
        email: row.contact_email || "", status: row.status, firstContact: row.first_contact_date || "",
        iconUrl: row.icon_url || "", website: row.website || "", owner: row.owner_text || "", notes: row.notes || ""
      })),
      agreements: raw.mou_actions.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, name: row.name,
        organizationId: organizationCodes.get(row.organization_id) || "",
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id) || "",
        owner: row.owner_text || "", status: row.status, start: row.start_date || "",
        targetSign: row.target_sign_date || "", effective: row.effective_date || "", expiry: row.expiry_date || "",
        nextAction: row.next_action_date || "", linkedGateId: gateCodes.get(row.linked_gate_id) || "",
        scope: row.scope || "", obligations: row.obligations || "", risk: row.risk_level || "Medium",
        fileRef: row.external_url || "", updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      events: raw.events.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, date: row.event_date,
        type: row.event_type, name: row.name, organizationId: organizationCodes.get(row.organization_id) || "",
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id) || "",
        owner: row.owner_text || "", status: row.status
      })),
      risks: raw.risks.map(row => ({
        id: row.code, _dbId: row.id, version: row.version,
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id) || "",
        gateId: gateCodes.get(row.gate_id) || "",
        category: row.category || "", description: row.description, probability: row.probability,
        impact: row.impact, owner: row.owner_text || "", response: row.response_plan || "",
        due: row.due_date || "", status: row.status, score: row.probability * row.impact,
        updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      decisions: raw.decisions.map(row => ({
        id: row.code, _dbId: row.id, version: row.version, date: row.decision_date,
        projectId: projectCodes.get(row.project_id), phaseId: phaseCodes.get(row.phase_id) || "",
        title: row.title, decision: row.decision_text, owner: row.owner_text || "",
        status: row.status, source: row.source || ""
      })),
      attachments: raw.attachments.map(row => ({
        id: row.id, _dbId: row.id, version: row.version,
        projectId: projectCodes.get(row.project_id) || "", activityId: activityCodes.get(row.activity_id) || "",
        gateId: gateCodes.get(row.gate_id) || "", entityType: row.entity_type || (row.activity_id ? "activity" : row.gate_id ? "gate" : "project"),
        entityId: row.entity_id || activityCodes.get(row.activity_id) || gateCodes.get(row.gate_id) || projectCodes.get(row.project_id) || "",
        title: row.title, url: row.web_view_link || row.url, storagePath: row.storage_path || "",
        provider: row.provider || "link", externalId: row.external_id || "", mimeType: row.mime_type || row.file_type || "",
        iconLink: row.icon_link || "", thumbnailLink: row.thumbnail_link || "", webViewLink: row.web_view_link || row.url,
        previewUrl: row.preview_url || "", modifiedTime: row.modified_time || "", metadata: row.metadata || {},
        snapshotStatus: row.snapshot_status || "not_requested", snapshotMimeType: row.snapshot_mime_type || "",
        snapshotSize: asNumber(row.snapshot_size), snapshotAt: row.snapshot_at || "", snapshotError: row.snapshot_error || "",
        sourceProfileId: row.source_profile_id || "", sourceGoogleEmail: row.source_google_email || "", sourceGoogleSubject: row.source_google_subject || "",
        revision: row.revision || "", documentStatus: row.document_status || "Draft", owner: row.owner_text || "",
        externalVisible: Boolean(row.external_visible), updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      googleCalendarEventLinks: raw.google_calendar_event_links.map(row => ({
        id: row.id, calendarId: row.calendar_id, eventId: row.google_event_id, iCalUID: row.google_ical_uid || "",
        projectId: projectCodes.get(row.project_id) || "", phaseId: phaseCodes.get(row.phase_id) || "",
        activityId: activityCodes.get(row.activity_id) || "", googleUpdatedAt: row.google_updated_at || "",
        url: row.google_event_url || "", snapshot: row.google_snapshot || {}, syncStatus: row.sync_status || "synced",
        sourceProfileId: row.source_profile_id || "", sourceGoogleEmail: row.source_google_email || "",
        lastSyncedAt: row.last_synced_at || ""
      })),
      strategicGoals: raw.strategic_goals.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, code: row.code,
        projectId: projectCodes.get(row.project_id) || "", name: row.name, goalType: row.goal_type || "strategic",
        horizon: row.horizon_date || "", priorProbability: asNumber(row.prior_probability), targetProbability: asNumber(row.target_probability),
        strategicValue: asNumber(row.strategic_value), owner: row.owner_text || "", status: row.status || "Active",
        notes: row.notes || "", tags: Array.isArray(row.tags) ? row.tags : [], updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      evidenceItems: raw.evidence_items.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, projectId: projectCodes.get(row.project_id) || "", goalId: row.goal_id || "",
        entityType: row.entity_type || "", entityId: row.entity_key || "", evidenceType: row.evidence_type || "document", title: row.title,
        direction: row.direction || "positive", strength: asNumber(row.strength), reliability: asNumber(row.reliability), relevance: asNumber(row.relevance),
        binding: asNumber(row.binding_strength), observedAt: row.observed_at || "", expiresAt: row.expires_at || "", sourceUrl: row.source_url || "",
        sourceRef: row.source_ref || "", summary: row.summary || "", metadata: row.metadata || {}, updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      entityLinks: raw.entity_links.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, projectId: projectCodes.get(row.project_id) || "", sourceType: row.source_type,
        sourceKey: row.source_key, targetType: row.target_type, targetKey: row.target_key, relationType: row.relation_type || "related",
        linkCategory: row.link_category || "semantic", linkStatus: row.link_status || "confirmed",
        weight: asNumber(row.weight), confidence: asNumber(row.confidence), polarity: row.polarity || "neutral", lagDays: Number(row.lag_days || 0),
        notes: row.notes || "", metadata: row.metadata || {}, updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      intelligencePredictions: raw.intelligence_predictions.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, projectId: projectCodes.get(row.project_id) || "", goalId: row.goal_id || "",
        predictedProbability: asNumber(row.predicted_probability), lowerProbability: asNumber(row.lower_probability), upperProbability: asNumber(row.upper_probability),
        confidence: asNumber(row.confidence), modelVersion: row.model_version || "naysor-rate-v1", rationale: row.rationale || {}, snapshot: row.snapshot || {},
        actualOutcome: row.actual_outcome || "", resolvedAt: row.resolved_at || "", createdAt: row.created_at || ""
      })),
      graphViews: raw.graph_views.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, profileId: row.profile_id, name: row.name, isDefault: Boolean(row.is_default),
        camera: row.camera || {}, filters: row.filters || {}, layout: row.layout || {}, updatedAt: row.updated_at
      })),
      teaProjects: raw.tea_projects.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, ownerId: row.owner_id,
        name: row.name, status: row.status || "Draft", currency: row.currency || "USD",
        projectData: row.project_data || {}, createdAt: row.created_at, updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      atlasNodePositions: raw.atlas_node_positions.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, projectId: projectCodes.get(row.project_id) || "",
        viewId: row.view_id || "", layoutMode: row.layout_mode, entityType: row.entity_type, entityKey: row.entity_key,
        x: row.x, y: row.y, groupId: row.group_id || "", collapsed: Boolean(row.collapsed),
        updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      ideas: raw.ideas.map(row => ({
        id: row.id, _dbId: row.id, version: row.version, projectId: projectCodes.get(row.project_id) || "",
        title: row.title, body: row.body || "", stage: row.stage || "idea",
        promotedActivityId: activityCodes.get(row.promoted_activity_id) || "", owner: row.owner_text || "",
        tags: Array.isArray(row.tags) ? row.tags : [], createdBy: row.created_by,
        updatedAt: row.updated_at, updatedBy: row.updated_by
      })),
      profiles: raw.profiles.map(row => {
        const membership = workspaceMembershipByProfile.get(row.id);
        return {
          id: row.id, full_name: row.full_name, email: row.email, title: row.title,
          organizationId: row.organization_id, avatar_url: row.avatar_url, language: row.language,
          timezone: row.timezone, active: row.active, last_seen_at: row.last_seen_at,
          systemRole: membership?.system_role || "viewer", workspaceMemberActive: Boolean(membership?.active),
          invitationStatus: row.invitation_status || (row.active ? "active" : "deactivated"),
          invitedAt: row.invited_at || "", welcomeEmailSentAt: row.welcome_email_sent_at || "",
          welcomeEmailError: row.welcome_email_error || "", welcomeEmailProvider: row.welcome_email_provider || "",
          welcomeEmailMessageId: row.welcome_email_message_id || "", mustChangePassword: Boolean(row.must_change_password),
          passwordChangedAt: row.password_changed_at || "", profileCompletedAt: row.profile_completed_at || "",
          deactivatedAt: row.deactivated_at || "", googleProfileName: row.google_profile_name || "",
          googleProfileAvatarUrl: row.google_profile_avatar_url || "", googleProfileSyncedAt: row.google_profile_synced_at || ""
        };
      }),
      projectMembers: raw.project_members.map(row => ({
        id: row.id, _dbId: row.id, version: row.version,
        projectId: projectCodes.get(row.project_id), profileId: row.profile_id,
        role: row.project_role, active: row.active
      })),
      audit: raw.audit_logs.map(row => ({
        id: row.id, at: row.changed_at, action: row.action,
        entity: row.table_name + ":" + row.record_id,
        detail: row.change_reason || "", userId: row.changed_by,
        projectId: projectCodes.get(row.project_id) || "", before: row.before_data, after: row.after_data
      }))
    };
    state.accessUsers = [];
    state.accessMailService = {};
    if (this.systemRole === "system_admin") {
      try {
        const access = await this.listManagedUsers();
        state.accessUsers = access.users || [];
        state.accessProjects = access.projects || [];
        state.accessMailService = access.mailService || {};
      } catch (error) {
        console.warn("User access status load failed", error);
      }
    }
    return state;
  }

  idFor(kind, legacyId) {
    if (!legacyId) return null;
    const rows = this.maps[kind];
    if (!rows) return null;
    for (const row of rows.values()) if (row.code === legacyId) return row.id;
    return null;
  }

  payloadFor(type, record) {
    const common = { workspace_id: this.workspaceId };
    if (type === "program") return withoutUndefined({
      ...common, code: record.code || record.id, name: record.name,
      objective: record.objective || "", owner_text: record.owner || "",
      start_date: record.start || null, finish_date: record.finish || null,
      as_of_date: record.asOfDate || null, currency: record.currency || "USD",
      calendar_name: record.calendar || "NAYSOR-5D",
      active_baseline_code: record.baseline || "BL-01",
      schema_version: record.schemaVersion || "2.0", app_version: record.appVersion || "5.0.0-poc.1"
    });
    if (type === "project") return withoutUndefined({
      ...common, program_id: this.programId, code: record.code || record.id, name: record.name,
      name_en: record.englishName || record.name, objective: record.objective, owner_text: record.owner,
      sponsor_text: record.sponsor, color: record.color, start_date: record.start || null,
      finish_date: record.finish || null, budget: asNumber(record.budget), status: record.status,
      completion_gate_code: record.completionGateId || null
    });
    if (type === "phase") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), code: record.code || record.id,
      order_no: Number(record.order || 0), name: record.name, name_ko: record.koreanName || record.name,
      objective: record.objective, owner_text: record.owner, start_date: record.start || null,
      finish_date: record.finish || null, outcome_code: record.outcomeId || null
    });
    if (type === "activity") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId),
      phase_id: this.idFor("phases", record.phaseId), code: record.id, wbs_code: record.wbs,
      name: record.name, description: record.description, owner_text: record.owner,
      assigned_to: record.assignedTo || null, organization_text: record.organization,
      start_date: record.start || null, finish_date: record.finish || null,
      baseline_start: record.baselineStart || null, baseline_finish: record.baselineFinish || null,
      actual_start: record.actualStart || null, actual_finish: record.actualFinish || null,
      forecast_start: record.forecastStart || null, forecast_finish: record.forecastFinish || null,
      progress: asNumber(record.progress), status: record.status, gate_code: record.gateId || null,
      priority: record.priority, product_stream: record.productStream, budget: asNumber(record.budget),
      actual_cost: asNumber(record.actualCost), forecast_cost: asNumber(record.forecastCost),
      planned_hours: asNumber(record.plannedHours), actual_hours: asNumber(record.actualHours),
      kpi: record.kpi, calendar_name: record.calendar, constraint_type: record.constraint,
      notes: record.notes, source: record.source
    });
    if (type === "assignment") {
      const activity = this.state?.activities?.find(item => item.id === record.activityId);
      return withoutUndefined({
        ...common,
        project_id: this.idFor("projects", activity?.projectId),
        code: record.id,
        activity_id: this.idFor("activities", record.activityId),
        resource_id: this.idFor("resources", record.resourceId),
        planned_hours: asNumber(record.plannedHours),
        actual_hours: asNumber(record.actualHours),
        role_text: record.role || ""
      });
    }
    if (type === "deliverable") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId),
      phase_id: this.idFor("phases", record.phaseId), activity_id: this.idFor("activities", record.activityId),
      gate_id: this.idFor("gates", record.gateId), code: record.code || record.id, name: record.name,
      deliverable_type: record.type, owner_text: record.owner, due_date: record.due || null,
      status: record.status, approval_status: record.approvalStatus,
      revision: record.versionLabel || (typeof record.version === "string" ? record.version : "v0.1"),
      external_url: record.fileRef || null, notes: record.notes
    });
    if (type === "agreement") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId),
      phase_id: this.idFor("phases", record.phaseId),
      organization_id: this.idFor("organizations", record.organizationId),
      linked_gate_id: this.idFor("gates", record.linkedGateId), code: record.id, name: record.name,
      owner_text: record.owner, status: record.status, start_date: record.start || null,
      target_sign_date: record.targetSign || null, effective_date: record.effective || null,
      expiry_date: record.expiry || null, next_action_date: record.nextAction || null,
      scope: record.scope, obligations: record.obligations, risk_level: record.risk,
      external_url: record.fileRef || null
    });
    if (type === "risk") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId),
      phase_id: this.idFor("phases", record.phaseId), gate_id: this.idFor("gates", record.gateId), code: record.id, category: record.category,
      description: record.description, probability: Number(record.probability), impact: Number(record.impact),
      owner_text: record.owner, response_plan: record.response, due_date: record.due || null, status: record.status
    });
    if (type === "gate") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId),
      after_phase_id: this.idFor("phases", record.afterPhaseId),
      before_phase_id: this.idFor("phases", record.beforePhaseId),
      code: record.code || record.id, name: record.name, target_date: record.targetDate || null,
      approver_text: record.approver, status: record.status, decision: record.decision || null,
      decision_date: record.decisionDate || null, notes: record.notes
    });
    if (type === "organization") return withoutUndefined({
      ...common, code: record.id, name: record.name, organization_type: record.type || "Partner",
      country: record.country || null, contact_name: record.contact || null, contact_email: record.email || null,
      status: record.status || "Active", first_contact_date: record.firstContact || null,
      icon_url: record.iconUrl || null, website: record.website || null, owner_text: record.owner || null,
      notes: record.notes || null
    });
    if (type === "strategic_goal") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), code: record.code || `GOAL-${Date.now()}`, name: record.name,
      goal_type: record.goalType || "strategic", horizon_date: record.horizon || null,
      prior_probability: asNumber(record.priorProbability ?? .45), target_probability: asNumber(record.targetProbability ?? .75),
      strategic_value: asNumber(record.strategicValue ?? 1), owner_text: record.owner || "", status: record.status || "Active",
      notes: record.notes || "", tags: Array.isArray(record.tags) ? record.tags : []
    });
    if (type === "evidence") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), goal_id: record.goalId || null,
      entity_type: record.entityType || null, entity_key: record.entityId || null, evidence_type: record.evidenceType || "document",
      title: record.title, direction: record.direction || "positive", strength: asNumber(record.strength ?? .5),
      reliability: asNumber(record.reliability ?? .5), relevance: asNumber(record.relevance ?? .5), binding_strength: asNumber(record.binding ?? .35),
      observed_at: record.observedAt || null, expires_at: record.expiresAt || null, source_url: record.sourceUrl || null,
      source_ref: record.sourceRef || null, summary: record.summary || "", metadata: record.metadata || {}
    });
    if (type === "entity_link") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), source_type: record.sourceType, source_key: record.sourceKey,
      target_type: record.targetType, target_key: record.targetKey, relation_type: record.relationType || "related",
      link_category: record.linkCategory || "semantic", link_status: record.linkStatus || "confirmed",
      weight: asNumber(record.weight ?? .5), confidence: asNumber(record.confidence ?? .5), polarity: record.polarity || "neutral",
      lag_days: Number(record.lagDays || 0), notes: record.notes || "", metadata: record.metadata || {}
    });
    if (type === "graph_view") return withoutUndefined({
      ...common, profile_id: record.profileId || this.profile.id, name: record.name || "My Workspace",
      is_default: Boolean(record.isDefault), camera: record.camera || {}, filters: record.filters || {}, layout: record.layout || {}
    });
    if (type === "tea_project") return withoutUndefined({
      ...common, owner_id: record.ownerId || this.profile.id, name: record.name || "Untitled TEA Project",
      status: record.status || "Draft", currency: record.currency || "USD", project_data: record.projectData || record
    });
    if (type === "intelligence_prediction") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), goal_id: record.goalId || null,
      predicted_probability: asNumber(record.predictedProbability), lower_probability: asNumber(record.lowerProbability),
      upper_probability: asNumber(record.upperProbability), confidence: asNumber(record.confidence ?? .3),
      model_version: record.modelVersion || "naysor-rate-v1", rationale: record.rationale || {}, snapshot: record.snapshot || {},
      actual_outcome: record.actualOutcome || null, resolved_at: record.resolvedAt || null
    });
    if (type === "atlas_node_position") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), view_id: record.viewId || null,
      layout_mode: record.layoutMode, entity_type: record.entityType, entity_key: record.entityKey,
      x: Number(record.x), y: Number(record.y), group_id: record.groupId || null, collapsed: Boolean(record.collapsed)
    });
    if (type === "idea") return withoutUndefined({
      ...common, project_id: this.idFor("projects", record.projectId), title: record.title, body: record.body || "",
      stage: record.stage || "idea", promoted_activity_id: this.idFor("activities", record.promotedActivityId),
      owner_text: record.owner || "", tags: Array.isArray(record.tags) ? record.tags : []
    });
    throw new Error("Unsupported entity type: " + type);
  }

  tableFor(type) {
    return {
      program: "programs", project: "projects", phase: "phases", activity: "activities", assignment: "resource_assignments",
      deliverable: "deliverables", agreement: "mou_actions", risk: "risks", gate: "gates", organization: "organizations",
      strategic_goal: "strategic_goals", evidence: "evidence_items", entity_link: "entity_links", intelligence_prediction: "intelligence_predictions", graph_view: "graph_views",
      atlas_node_position: "atlas_node_positions", idea: "ideas", tea_project: "tea_projects"
    }[type];
  }

  async persistEntity(type, record, previous = null) {
    if (!navigator.onLine) throw new Error("Offline writes are disabled.");
    const table = this.tableFor(type);
    if (!table) throw new Error("Unsupported entity type: " + type);
    const payload = this.payloadFor(type, record);
    let result;
    if (previous?._dbId || record._dbId) {
      const id = previous?._dbId || record._dbId;
      const expectedVersion = Number(previous?.version || record.version || 1);
      result = await this.client
        .from(table)
        .update(payload)
        .eq("id", id)
        .eq("version", expectedVersion)
        .select("id,version,updated_at,updated_by")
        .maybeSingle();
      if (!result.error && !result.data) {
        const { data: latest } = await this.client.from(table).select("*").eq("id", id).maybeSingle();
        throw new ConflictError("Version conflict", latest);
      }
    } else {
      result = await this.client
        .from(table)
        .insert(payload)
        .select("id,version,updated_at,updated_by")
        .single();
    }
    if (result.error) {
      if (result.error.code === "42501") throw new PermissionError(result.error.message);
      throw result.error;
    }
    const saved = {
      ...record,
      _dbId: result.data.id,
      version: result.data.version,
      updatedAt: result.data.updated_at,
      updatedBy: result.data.updated_by
    };
    if (["strategic_goal","evidence","entity_link","intelligence_prediction","graph_view","atlas_node_position","idea","tea_project"].includes(type)) saved.id = result.data.id;
    if (type === "activity") await this.replaceDependencies(saved);
    if (type === "program") this.programId = saved._dbId;
    const mapName = { project: "projects", phase: "phases", activity: "activities", gate: "gates", organization: "organizations" }[type];
    if (mapName && saved._dbId) this.maps[mapName]?.set(saved._dbId, { ...(this.maps[mapName]?.get(saved._dbId) || {}), id: saved._dbId, code: saved.code || saved.id });
    return saved;
  }

  async replaceDependencies(activity) {
    const dependencies = String(activity.predecessors || "").split(/[,;]+/).map(value => value.trim()).filter(Boolean);
    const { error } = await this.client.rpc("replace_activity_dependencies", {
      p_activity_id: activity._dbId,
      p_dependencies: dependencies
    });
    if (error) throw error;
  }

  async persistMany(type, records) {
    const saved = [];
    for (const record of records) saved.push(await this.persistEntity(type, record, record));
    return saved;
  }

  async softDeleteEntity(type, record) {
    const table = this.tableFor(type);
    const { data, error } = await this.client
      .from(table)
      .update({ deleted_at: new Date().toISOString(), deleted_by: this.profile.id })
      .eq("id", record._dbId)
      .eq("version", Number(record.version || 1))
      .select("id")
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new ConflictError("Version conflict");
    return true;
  }

  async updateGateChecklist(gate, item) {
    const payload = {
      completed: Boolean(item.completed),
      evidence_text: item.evidence || "",
      owner_text: item.owner || "",
      due_date: item.due || null
    };
    const { data, error } = await this.client
      .from("gate_checklists")
      .update(payload)
      .eq("id", item._dbId)
      .eq("version", Number(item.version || 1))
      .select("id,version,updated_at,updated_by")
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new ConflictError("Version conflict");
    return {...item, version: data.version, updatedAt: data.updated_at, updatedBy: data.updated_by};
  }

  async decideGate(gate, decision) {
    const { data, error } = await this.client.rpc("decide_gate", {
      p_gate_id: gate._dbId,
      p_expected_version: Number(gate.version || 1),
      p_decision: decision.decision,
      p_notes: decision.notes || ""
    });
    if (error) {
      if (error.message?.includes("VERSION_CONFLICT")) throw new ConflictError(error.message);
      throw error;
    }
    return {...gate, ...decision, version: data.version, updatedAt: data.updated_at, updatedBy: data.updated_by};
  }

  async importSnapshot(snapshot) {
    const { error } = await this.client.rpc("import_naysor_snapshot", { p_snapshot: snapshot });
    if (error) throw error;
    if (Object.prototype.hasOwnProperty.call(snapshot || {}, "strategicGoals")) {
      const intelligence = await this.client.rpc("import_naysor_intelligence_snapshot", { p_snapshot: snapshot });
      if (intelligence.error) throw intelligence.error;
    }
    return this.reload();
  }

  accessAdminErrorMessage(code) {
    const value = String(code || "");
    if (value === "USER_ALREADY_ACTIVE") return "이미 활성화된 사용자입니다. 사용자 목록에서 권한을 수정하세요.";
    if (value === "PASSWORD_ALREADY_CHANGED") return "이미 초기 비밀번호를 변경한 사용자입니다. 초대 메일을 다시 보내지 않았습니다.";
    if (value === "USER_DEACTIVATED_USE_REINVITE") return "내보낸 사용자입니다. '다시 초대' 기능을 사용하세요.";
    if (value === "ADMIN_GMAIL_SEND_SCOPE_REQUIRED") return "관리자 Google 계정에 Gmail 발송 권한이 없습니다. 내 연결에서 Google 권한을 다시 승인하세요.";
    if (value === "ADMIN_GOOGLE_CONNECTION_REQUIRED") return "초대 메일을 보낼 System Admin Google 계정이 연결되어 있지 않습니다.";
    if (value === "VALID_EMAIL_REQUIRED") return "올바른 이메일 주소를 입력하세요.";
    if (value === "DEACTIVATE_BEFORE_ARCHIVE") return "활성 사용자는 바로 기록 삭제할 수 없습니다. 먼저 내보내기를 실행하세요.";
    if (value === "CANNOT_ARCHIVE_SELF") return "현재 로그인한 관리자 자신의 사용자 기록은 삭제할 수 없습니다.";
    if (value.startsWith("INITIAL_PASSWORD_REJECTED:")) return `초기 비밀번호를 Supabase Auth가 허용하지 않았습니다. Auth 비밀번호 정책의 최소 길이를 확인하세요. (${value.split(":").slice(1).join(":")})`;
    return value;
  }

  async accessAdmin(mode, payload = {}) {
    const { data, error } = await this.client.functions.invoke("admin-invite-user", {
      body: { mode, ...payload, workspaceId: this.workspaceId }
    });
    if (error) {
      let detail = "";
      try {
        const errorPayload = await error.context?.json?.();
        detail = errorPayload?.error || "";
      } catch {
        detail = "";
      }
      const candidate = detail || error.message || "사용자 권한 작업에 실패했습니다.";
      const raw = typeof candidate === "string" ? candidate : JSON.stringify(candidate);
      throw new Error(this.accessAdminErrorMessage(raw));
    }
    if (data?.error) throw new Error(this.accessAdminErrorMessage(data.error));
    return data;
  }

  async listManagedUsers() {
    return this.accessAdmin("list");
  }

  async inviteUser(invitation) {
    return this.accessAdmin("invite", invitation);
  }

  async updateUserAccess(userId, systemRole, projectAccess) {
    return this.accessAdmin("update_access", { userId, systemRole, projectAccess });
  }

  async deactivateUser(userId) {
    return this.accessAdmin("deactivate", { userId });
  }

  async reactivateUser(userId, systemRole, projectAccess) {
    return this.accessAdmin("reactivate", { userId, systemRole, projectAccess });
  }

  async resendInvitation(userId) {
    return this.accessAdmin("resend", { userId });
  }

  async archiveUserRecord(userId) {
    return this.accessAdmin("archive_record", { userId });
  }

  async updatePassword(password) {
    const { error } = await this.client.auth.updateUser({ password });
    if (error) throw error;
    const markResult = typeof this.client.rpc === "function" ? await this.client.rpc("mark_my_password_changed") : { data: null, error: null };
    const { data: changedAt, error: markError } = markResult;
    if (markError) console.warn("Password lifecycle mark failed", markError);
    this.profile.must_change_password = false;
    this.profile.password_changed_at = changedAt || new Date().toISOString();
    return true;
  }

  async updateProfile(changes) {
    const allowed = {
      full_name: changes.full_name,
      title: changes.title,
      avatar_url: changes.avatar_url,
      language: changes.language,
      timezone: changes.timezone
    };
    const { data, error } = await this.client
      .from("profiles")
      .update(allowed)
      .eq("id", this.profile.id)
      .select("*")
      .single();
    if (error) throw error;
    Object.assign(this.profile, data);
    const { data: completedAt, error: completedError } = await this.client.rpc("mark_my_profile_completed");
    if (!completedError) this.profile.profile_completed_at = completedAt;
    if (this.presenceChannel) {
      try { await this.presenceChannel.track(this.presencePayload()); } catch (presenceError) { console.warn("Presence profile refresh failed", presenceError); }
    }
    return this.profile;
  }

  async uploadAvatar(file) {
    if (!file || !String(file.type).startsWith("image/")) throw new Error("이미지 파일만 업로드할 수 있습니다.");
    if (file.size > 2 * 1024 * 1024) throw new Error("프로필 이미지는 2 MB 이하여야 합니다.");
    const extension = String(file.name).split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
    const path = `${this.profile.id}/avatar.${extension}`;
    const { error } = await this.client.storage.from("naysor-avatars").upload(path, file, { upsert: true, contentType: file.type, cacheControl: "3600" });
    if (error) throw error;
    const { data } = this.client.storage.from("naysor-avatars").getPublicUrl(path);
    return `${data.publicUrl}?v=${Date.now()}`;
  }

  projectCodeForEntity(entityType, entityId) {
    if (entityType === "project") return entityId;
    if (entityType === "phase") return this.state?.phases?.find(item => item.id === entityId)?.projectId || "";
    if (entityType === "activity") return this.state?.activities?.find(item => item.id === entityId)?.projectId || "";
    if (entityType === "gate") return this.state?.gates?.find(item => item.id === entityId)?.projectId || "";
    if (entityType === "deliverable") return this.state?.deliverables?.find(item => item.id === entityId)?.projectId || "";
    if (entityType === "agreement") return this.state?.agreements?.find(item => item.id === entityId)?.projectId || "";
    if (entityType === "risk") return this.state?.risks?.find(item => item.id === entityId)?.projectId || "";
    if (entityType === "organization") {
      return this.state?.agreements?.find(item => item.organizationId === entityId)?.projectId
        || this.state?.events?.find(item => item.organizationId === entityId)?.projectId
        || this.state?.projects?.[0]?.id || "";
    }
    return "";
  }

  async createExternalAttachment(entityType, entityId, file) {
    const projectCode = file.projectId || this.projectCodeForEntity(entityType, entityId);
    const payload = {
      workspace_id: this.workspaceId,
      project_id: projectCode ? this.idFor("projects", projectCode) : null,
      activity_id: entityType === "activity" ? this.idFor("activities", entityId) : null,
      gate_id: entityType === "gate" ? this.idFor("gates", entityId) : null,
      entity_type: entityType,
      entity_id: entityId,
      title: file.name || file.title || "Google Drive file",
      url: file.webViewLink || file.url || file.previewUrl,
      provider: file.provider || "google_drive",
      external_id: file.id || file.externalId || null,
      file_type: file.kind || file.fileType || null,
      mime_type: file.mimeType || null,
      icon_link: file.iconLink || null,
      thumbnail_link: file.thumbnailLink || null,
      web_view_link: file.webViewLink || file.url || null,
      preview_url: file.previewUrl || null,
      modified_time: file.modifiedTime || null,
      metadata: file.metadata || {},
      source_profile_id: String(file.provider || "").startsWith("google_") ? (file.sourceProfileId || this.profile.id) : null,
      source_google_email: String(file.provider || "").startsWith("google_") ? (file.sourceGoogleEmail || file.googleEmail || "") : null,
      source_google_subject: String(file.provider || "").startsWith("google_") ? (file.sourceGoogleSubject || "") : null,
      document_status: file.documentStatus || "Linked",
      owner_text: file.owner || this.profile.full_name || "",
      external_visible: Boolean(file.externalVisible)
    };
    let existing = null;
    if (payload.external_id) {
      const lookup = await this.client.from("attachments")
        .select("id,version")
        .eq("workspace_id", this.workspaceId)
        .eq("provider", payload.provider)
        .eq("external_id", payload.external_id)
        .eq("source_profile_id", payload.source_profile_id)
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .is("deleted_at", null)
        .limit(1)
        .maybeSingle();
      if (lookup.error) throw lookup.error;
      existing = lookup.data;
    }
    const query = existing
      ? this.client.from("attachments").update({ ...payload, updated_by: this.profile.id, deleted_at: null, deleted_by: null }).eq("id", existing.id)
      : this.client.from("attachments").insert({ ...payload, created_by: this.profile.id, updated_by: this.profile.id });
    const { data, error } = await query.select("*").single();
    if (error) throw error;
    await this.reload();
    return data;
  }

  async deleteExternalAttachment(attachmentId) {
    const { error } = await this.client.from("attachments").update({
      deleted_at: new Date().toISOString(), deleted_by: this.profile.id
    }).eq("id", attachmentId);
    if (error) throw error;
    await this.reload();
    return true;
  }


  async askNaysorAI(question, context = {}, options = {}) {
    const { data, error } = await this.client.functions.invoke("naysor-ai", {
      body: { workspaceId: this.workspaceId, question, context, webSearch: options.webSearch !== false }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async searchMarketEquipment(request = {}) {
    const data = await invokeFunction(this.client, "market-search", { ...request, workspaceId: this.workspaceId });
    return data || { results: [] };
  }

  /** Whether the search provider is actually configured, and which secret is
   *  missing when it is not — so the studio can say so instead of failing. */
  async marketSearchStatus() {
    return invokeFunction(this.client, "market-search", { workspaceId: this.workspaceId, mode: "status" });
  }

  async saveMarketReference(request = {}) {
    return invokeFunction(this.client, "market-search", { ...request, workspaceId: this.workspaceId, mode: "save_reference" });
  }

  async askNaysorAIStream(question, context = {}, options = {}, handlers = {}) {
    const { data: sessionData, error: sessionError } = await this.client.auth.getSession();
    if (sessionError) throw sessionError;
    const accessToken = sessionData?.session?.access_token || this.session?.access_token || "";
    if (!accessToken) throw new Error("AUTH_REQUIRED");
    const endpoint = `${String(this.config?.supabaseUrl || "").replace(/\/+$/, "")}/functions/v1/naysor-ai`;
    if (!endpoint.startsWith("http")) throw new Error("SUPABASE_FUNCTION_URL_MISSING");
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: this.config?.supabaseKey || "",
        "Content-Type": "application/json",
        Accept: "text/event-stream"
      },
      body: JSON.stringify({
        workspaceId: this.workspaceId,
        question,
        context,
        webSearch: options.webSearch !== false,
        forceWebSearch: options.forceWebSearch === true,
        stream: true
      })
    });
    if (!response.ok) {
      let payload = {};
      try { payload = await response.json(); } catch { payload = { error: await response.text().catch(() => "") }; }
      throw new Error(payload?.error || `NAYSOR_AI_HTTP_${response.status}`);
    }
    const contentType = String(response.headers.get("content-type") || "");
    if (!contentType.includes("text/event-stream") || !response.body) {
      const data = await response.json();
      if (data?.error) throw new Error(data.error);
      handlers.onMeta?.(data);
      if (data?.answer) handlers.onDelta?.(String(data.answer));
      handlers.onDone?.(data);
      return data;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let donePayload = null;
    const dispatch = (block) => {
      if (!block.trim()) return;
      let eventName = "message";
      const dataLines = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
      }
      if (!dataLines.length) return;
      let data;
      try { data = JSON.parse(dataLines.join("\n")); } catch { data = { raw: dataLines.join("\n") }; }
      if (eventName === "meta") handlers.onMeta?.(data);
      else if (eventName === "status") handlers.onStatus?.(data);
      else if (eventName === "delta") handlers.onDelta?.(String(data?.delta || ""));
      else if (eventName === "done") { donePayload = data; handlers.onDone?.(data); }
      else if (eventName === "error") throw new Error(data?.error || "NAYSOR_AI_STREAM_ERROR");
      else handlers.onEvent?.(eventName, data);
    };
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
      let boundary;
      while ((boundary = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        dispatch(block);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) dispatch(buffer);
    return donePayload || { streamed: true };
  }

  async getNaysorAIStatus() {
    const { data, error } = await this.client.functions.invoke("naysor-ai", {
      body: { workspaceId: this.workspaceId, mode: "status" }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data || {};
  }

  async translateInterfaceBatch(items = [], targetLanguage = "en") {
    const payload = (Array.isArray(items) ? items : []).slice(0, 100).map((item, index) => ({
      id: String(item?.id || index),
      text: String(item?.text || "")
    }));
    const { data, error } = await this.client.functions.invoke("naysor-ai", {
      body: { workspaceId: this.workspaceId, mode: "translate", targetLanguage, items: payload }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data || { items: [] };
  }

  async loadNaysorAIHistory(before = "", limit = 30) {
    const { data, error } = await this.client.functions.invoke("naysor-ai", {
      body: { workspaceId: this.workspaceId, mode: "history", before, limit }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async analyzeEvidenceAttachment(attachmentId, goalId = "") {
    const { data, error } = await this.client.functions.invoke("naysor-ai", {
      body: { workspaceId: this.workspaceId, mode: "analyze_evidence", attachmentId, goalId }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  }

  subscribe(onChange, onPresence, onStatus) {
    this.onChange = onChange;
    this.onPresence = onPresence;
    this.onStatus = onStatus;
    this.startDataChannel();
    this.startPresenceChannel();
    return () => {
      if (this.dataChannel) this.client.removeChannel(this.dataChannel);
      if (this.presenceChannel) this.client.removeChannel(this.presenceChannel);
      this.dataChannel = null;
      this.presenceChannel = null;
    };
  }

  async startDataChannel() {
    if (this.dataChannel) await this.client.removeChannel(this.dataChannel);
    const projectId = this.projectScope === "ALL" ? null : this.idFor("projects", this.projectScope);
    const name = "naysor-data:" + this.workspaceId + ":" + this.projectScope;
    let channel = this.client.channel(name, {
      config: { broadcast: { self: false } }
    });
    // graph_views are profile-scoped personal layout state, not project-scoped
    // execution data. Saving already reconciles them locally; subscribing with
    // the generic project_id filter is invalid and can cause refresh loops.
    const tables = ["projects", "phases", "activities", "activity_dependencies", "gates", "gate_checklists", "deliverables", "resources", "resource_assignments", "costs", "risks", "decisions", "mou_actions", "comments", "attachments", "notifications", "google_calendar_sync_settings", "google_calendar_event_links", "strategic_goals", "evidence_items", "entity_links", "intelligence_predictions", "atlas_node_positions", "ideas"];
    tables.forEach(table => {
      const filter = !projectId
        ? "workspace_id=eq." + this.workspaceId
        : table === "projects" ? "id=eq." + projectId : "project_id=eq." + projectId;
      channel = channel.on("postgres_changes", {
        event: "*", schema: "public", table, filter
      }, payload => this.scheduleChange({...payload, table}));
    });
    channel = channel
      .on("broadcast", { event: "editing" }, payload => {
        this.onChange?.({ table: "presence", eventType: "EDITING", new: payload.payload });
      })
      .subscribe(status => {
        if (status === "SUBSCRIBED") {
          this.onStatus?.("online");
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          this.onStatus?.("offline");
        } else if (status === "CLOSED") {
          this.onStatus?.("connecting");
        }
      });
    this.dataChannel = channel;
  }

  async startPresenceChannel() {
    if (this.presenceChannel) await this.client.removeChannel(this.presenceChannel);
    const channel = this.client.channel("naysor-presence:" + this.workspaceId, {
      config: { presence: { key: this.profile.id } }
    });
    channel
      .on("presence", { event: "sync" }, () => {
        const users = [...new Map(Object.values(channel.presenceState()).flat().map(value => [value.user_id, {
          id: value.user_id,
          full_name: value.full_name,
          avatar_url: value.avatar_url,
          email: value.email,
          role: value.role,
          online_at: value.online_at
        }])).values()];
        this.onPresence?.(users);
      })
      .subscribe(async status => {
        if (status !== "SUBSCRIBED") return;
        await channel.track(this.presencePayload());
      });
    this.presenceChannel = channel;
  }

  presencePayload() {
    return {
      user_id: this.profile.id,
      full_name: this.profile.full_name,
      avatar_url: this.profile.avatar_url,
      email: this.profile.email,
      role: this.systemRole,
      online_at: new Date().toISOString()
    };
  }

  scheduleChange(payload) {
    clearTimeout(this.changeTimer);
    this.changeTimer = setTimeout(() => this.onChange?.(payload), 180);
  }

  setProjectScope(projectCode) {
    if (projectCode === this.projectScope) return;
    this.projectScope = projectCode || "ALL";
    this.startDataChannel();
  }

  setEditing(activityId, editing = true) {
    if (!this.dataChannel) return;
    this.dataChannel.send({
      type: "broadcast",
      event: "editing",
      payload: {
        activityId,
        editing,
        userId: this.profile.id,
        userName: this.profile.full_name,
        at: new Date().toISOString()
      }
    });
  }

  reconnect() {
    this.startDataChannel();
    this.startPresenceChannel();
  }
}
